import React from "react";
import { makeStyles } from "@material-ui/core/styles";
import Box from "@material-ui/core/Box";
import Typography from "@material-ui/core/Typography";
import TextField from "@material-ui/core/TextField";
import Button from "@material-ui/core/Button";
import Divider from "@material-ui/core/Divider";
import CircularProgress from "@material-ui/core/CircularProgress";

const API_URL = "/.netlify/functions/comments";

const useStyles = makeStyles((theme) => ({
  commentsSection: {
    maxWidth: 800,
    margin: "0 auto",
    padding: theme.spacing(2),
    color: "white",
  },
  divider: {
    backgroundColor: "rgba(255, 255, 255, 0.2)",
    marginBottom: theme.spacing(3),
  },
  form: {
    marginBottom: theme.spacing(4),
  },
  textField: {
    marginBottom: theme.spacing(2),
    "& .MuiInputBase-root": {
      color: "white",
      backgroundColor: "#1a1a1a",
    },
    "& .MuiInputLabel-root": {
      color: "rgba(255, 255, 255, 0.6)",
    },
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: "rgba(255, 255, 255, 0.3)",
    },
    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: "rgba(255, 255, 255, 0.5)",
    },
  },
  submitButton: {
    color: "white",
    borderColor: "rgba(255, 255, 255, 0.5)",
  },
  honeypot: {
    position: "absolute",
    left: "-9999px",
    opacity: 0,
    height: 0,
    overflow: "hidden",
  },
  comment: {
    backgroundColor: "#1a1a1a",
    borderRadius: "0.5rem",
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  commentHeader: {
    display: "flex",
    justifyContent: "space-between",
    flexWrap: "wrap",
    marginBottom: theme.spacing(1),
  },
  commentName: {
    fontWeight: "bold",
  },
  commentDate: {
    color: "rgba(255, 255, 255, 0.5)",
  },
  commentText: {
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
    lineHeight: 1.6,
  },
  statusText: {
    color: "rgba(255, 255, 255, 0.6)",
    marginBottom: theme.spacing(2),
  },
  errorText: {
    color: "#f48771",
    marginBottom: theme.spacing(2),
  },
}));

const Comments = ({ articleName }) => {
  const classes = useStyles();
  const [comments, setComments] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [name, setName] = React.useState("");
  const [text, setText] = React.useState("");
  const [honeypot, setHoneypot] = React.useState("");
  const [submitting, setSubmitting] = React.useState(false);
  const [error, setError] = React.useState("");

  React.useEffect(() => {
    let cancelled = false;

    const loadComments = async () => {
      try {
        const response = await fetch(
          `${API_URL}?article=${encodeURIComponent(articleName)}`
        );
        if (!response.ok) throw new Error("Failed to load comments");
        const data = await response.json();
        if (!cancelled) setComments(data);
      } catch (err) {
        if (!cancelled) setError("Couldn't load comments right now.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadComments();
    return () => {
      cancelled = true;
    };
  }, [articleName]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!text.trim() || submitting) return;

    setSubmitting(true);
    setError("");
    try {
      const response = await fetch(
        `${API_URL}?article=${encodeURIComponent(articleName)}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ name, text, website: honeypot }),
        }
      );
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || "Failed to post comment");
      }
      const updated = await response.json();
      if (Array.isArray(updated)) setComments(updated);
      setText("");
    } catch (err) {
      setError(err.message || "Failed to post comment.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box className={classes.commentsSection}>
      <Divider className={classes.divider} />
      <Typography variant="h5" gutterBottom>
        Comments
      </Typography>

      <form className={classes.form} onSubmit={handleSubmit}>
        <TextField
          className={classes.textField}
          label="Name (optional)"
          variant="outlined"
          fullWidth
          value={name}
          onChange={(e) => setName(e.target.value)}
          inputProps={{ maxLength: 60 }}
        />
        {/* Honeypot: hidden from real users, catches spam bots */}
        <input
          className={classes.honeypot}
          type="text"
          tabIndex={-1}
          autoComplete="off"
          aria-hidden="true"
          value={honeypot}
          onChange={(e) => setHoneypot(e.target.value)}
        />
        <TextField
          className={classes.textField}
          label="Leave a comment"
          variant="outlined"
          fullWidth
          multiline
          minRows={3}
          value={text}
          onChange={(e) => setText(e.target.value)}
          inputProps={{ maxLength: 2000 }}
        />
        {error && (
          <Typography variant="body2" className={classes.errorText}>
            {error}
          </Typography>
        )}
        <Button
          type="submit"
          variant="outlined"
          className={classes.submitButton}
          disabled={submitting || !text.trim()}
        >
          {submitting ? "Posting..." : "Post Comment"}
        </Button>
      </form>

      {loading ? (
        <CircularProgress size={24} style={{ color: "white" }} />
      ) : comments.length === 0 ? (
        <Typography variant="body2" className={classes.statusText}>
          No comments yet. Be the first!
        </Typography>
      ) : (
        comments
          .slice()
          .reverse()
          .map((comment) => (
            <Box key={comment.id} className={classes.comment}>
              <Box className={classes.commentHeader}>
                <Typography variant="subtitle2" className={classes.commentName}>
                  {comment.name}
                </Typography>
                <Typography variant="caption" className={classes.commentDate}>
                  {new Date(comment.date).toLocaleDateString(undefined, {
                    year: "numeric",
                    month: "short",
                    day: "numeric",
                  })}
                </Typography>
              </Box>
              <Typography variant="body2" className={classes.commentText}>
                {comment.text}
              </Typography>
            </Box>
          ))
      )}
    </Box>
  );
};

export default Comments;
